import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

/**
 * Admin-only clean-air calibration for the analog gas sensors (CO, mercaptan).
 * Sends CALGAS over serial to capture a fresh per-device R0 baseline. The
 * device must be sitting in known-clean air when this runs.
 */
export default function CalibrateGasCard({ serialClient, onError = undefined }) {
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleCalibrate = useCallback(async () => {
    setBusy(true);
    setSuccess(false);
    setError(null);
    try {
      await serialClient.calibrateGas();
      setSuccess(true);
    } catch (err) {
      const message = err.message || 'Calibration failed';
      setError(message);
      if (onError) onError(message);
    } finally {
      setBusy(false);
    }
  }, [serialClient, onError]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Calibrate Gas Sensors (admin)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Capture a fresh clean-air baseline for the CO and mercaptan sensors.
          Run this only with the device in known-clean air.
        </Typography>

        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <TuneIcon />}
          onClick={handleCalibrate}
          disabled={busy}
        >
          {busy ? 'Calibrating...' : 'Calibrate gas sensors'}
        </Button>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Calibration complete. Clean-air baseline saved to the device.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

CalibrateGasCard.propTypes = {
  serialClient: PropTypes.object.isRequired,
  onError: PropTypes.func,
};
